import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Nav, Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import moment from 'moment';
import { Country, State, City } from "country-state-city";
import {
  Layers, Sliders, ArrowLeft, Save, Building2, User, FileText
} from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';

import Header from '../Include/Header';
import Sidebar from '../Include/Sidebar';
import Footer from '../Include/Footer';
import Loader from '../Include/Loader';

import AssemblyTreeBuilder from './components/AssemblyTreeBuilder';
import TechnicalSpecMatrix from './components/TechnicalSpecMatrix';
import CostingTaxSummary from './components/CostingTaxSummary';
import TermsAndDrawingTab from './components/TermsAndDrawingTab';

import { addAdminBom } from '../../../Store/Admin/believe/bom/AddBom';
import { updateAdminBom } from '../../../Store/Admin/believe/bom/UpdateBom';
import { getAdminOneBom, resetGetAdminOneBom } from '../../../Store/Admin/believe/bom/GetOneBom';
import { getFirm } from '../../../Store/Admin/Firm/Firm';
import { getAdminItem } from '../../../Store/Store/Item/AdminItem';
import { numberToWords, formatINR } from '../../../helper/bom';
import axios from 'axios';
import { V_URL } from '../../../BaseUrl';

// Clean, blank default state - No pre-filled mock data
const initialBomState = {
  firm_id: "",
  client_id: "",
  client_snapshot: {
    name: "",
    attention_person: "",
    email: "",
    phone: "",
    address: "",
    city: "Surat",
    state: "Gujarat",
    pincode: "",
    gst_no: ""
  },
  project_id: "",
  project_name: "",
  subject: "",
  quotation_ref_no: "",
  date: moment().format("YYYY-MM-DD"),
  status: "Draft",
  packaging_charges: "",
  packaging_gst_percent: 18,
  loading_charges: "",
  loading_gst_percent: 18,
  freight_charges: "",
  freight_gst_percent: 18,
  other_charges: "",
  gst_percent: 18,
  terms_template_name: "",
  terms_and_conditions: [],
  custom_terms_rich_text: "",
  drawing_details: {
    drawing_no: "",
    sheet_no: "01 OF 01",
    revision: "00",
    title: "",
    dimensions: { height: "", width: "", depth: "", base_height: "", unit: "mm" },
    notes: ""
  }
};

const ManageBom = () => {
  const { state: locationData } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Country State City Setup (India)
  const indiaCountry = Country.getAllCountries().find((c) => c.name === "India");
  const indiaCode = indiaCountry?.isoCode || "IN";

  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [activeTab, setActiveTab] = useState("info");
  const [formData, setFormData] = useState(initialBomState);
  const [assemblies, setAssemblies] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNewRevision, setIsNewRevision] = useState(false);
  const [revisionSummary, setRevisionSummary] = useState("");

  const firmData = useSelector((state) => state?.getFirm?.user?.data);
  const editData = useSelector((state) => state?.getAdminOneBom?.data?.data);
  const itemMasterList = useSelector((state) => state?.getAdminItem?.user?.data || []);

  useEffect(() => {
    if (indiaCode) {
      setStateList(State.getStatesOfCountry(indiaCode));
    }
  }, [indiaCode]);

  useEffect(() => {
    if (formData.client_snapshot?.state && indiaCode) {
      const selectedStateCode = State.getStatesOfCountry(indiaCode).find(
        (st) => st.name === formData.client_snapshot.state
      )?.isoCode;

      if (selectedStateCode) {
        setCityList(City.getCitiesOfState(indiaCode, selectedStateCode));
      } else {
        setCityList([]);
      }
    } else {
      setCityList([]);
    }
  }, [formData.client_snapshot?.state, indiaCode]);

  useEffect(() => {
    dispatch(getFirm());
    dispatch(getAdminItem({ is_main: true }));
    fetchAllClients();
  }, []);

  const fetchAllClients = async () => {
    try {
      const headers = { Authorization: "Bearer " + localStorage.getItem("VA_TOKEN") };
      const [resClient, resParty] = await Promise.allSettled([
        axios.get(`${V_URL}/admin/get-admin-client`, { headers }),
        axios.get(`${V_URL}/admin/get-admin-party`, { headers })
      ]);

      const list = [];
      const seenNames = new Set();

      if (resClient.status === "fulfilled" && resClient.value?.data?.data) {
        const cData = Array.isArray(resClient.value.data.data) ? resClient.value.data.data : [];
        cData.forEach(c => {
          if (c.name && !seenNames.has(c.name.toLowerCase().trim())) {
            seenNames.add(c.name.toLowerCase().trim());
            list.push({
              _id: c._id,
              name: c.name,
              attention_person: c.name,
              email: c.email || "",
              phone: c.phone ? String(c.phone) : "",
              address: c.address || "",
              city: "Surat",
              state: "Gujarat",
              gstNumber: c.gstNumber || ""
            });
          }
        });
      }

      if (resParty.status === "fulfilled" && resParty.value?.data?.data) {
        const pData = Array.isArray(resParty.value.data.data) ? resParty.value.data.data : [];
        pData.forEach(p => {
          if (p.name && !seenNames.has(p.name.toLowerCase().trim())) {
            seenNames.add(p.name.toLowerCase().trim());
            list.push({
              _id: p._id,
              name: p.name,
              attention_person: p.name,
              email: p.email || "",
              phone: p.phone ? String(p.phone) : "",
              address: p.address || "",
              city: p.city || "Surat",
              state: p.state || "Gujarat",
              gstNumber: p.gstNumber || p.gst_no || ""
            });
          }
        });
      }

      setClientOptions(list);
    } catch (err) {
      console.error("Error fetching client master list:", err);
    }
  };

  useEffect(() => {
    if (firmData && firmData.length > 0 && !formData.firm_id) {
      setFormData(prev => ({ ...prev, firm_id: firmData[0]._id }));
    }
  }, [firmData]);

  useEffect(() => {
    if (locationData?.id) {
      dispatch(getAdminOneBom(locationData.id));
    } else {
      dispatch(resetGetAdminOneBom());
    }
  }, [locationData]);

  useEffect(() => {
    if (editData && locationData?.id) {
      setFormData({
        firm_id: editData.firm_id?._id || editData.firm_id || "",
        client_id: editData.client_id?._id || editData.client_id || "",
        client_snapshot: editData.client_snapshot || initialBomState.client_snapshot,
        project_id: editData.project_id?._id || editData.project_id || "",
        project_name: editData.project_name || "",
        subject: editData.subject || "",
        quotation_ref_no: editData.quotation_ref_no || "",
        date: editData.date ? moment(editData.date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        status: editData.status || "Draft",
        packaging_charges: editData.packaging_charges !== undefined ? editData.packaging_charges : "",
        packaging_gst_percent: editData.packaging_gst_percent || 18,
        loading_charges: editData.loading_charges !== undefined ? editData.loading_charges : "",
        loading_gst_percent: editData.loading_gst_percent || 18,
        freight_charges: editData.freight_charges !== undefined ? editData.freight_charges : "",
        freight_gst_percent: editData.freight_gst_percent || 18,
        other_charges: editData.other_charges !== undefined ? editData.other_charges : "",
        gst_percent: editData.gst_percent || 18,
        terms_template_name: editData.terms_template_name || "",
        terms_and_conditions: editData.terms_and_conditions || [],
        custom_terms_rich_text: editData.custom_terms_rich_text || "",
        drawing_details: editData.drawing_details || initialBomState.drawing_details
      });
      if (editData.assemblies && editData.assemblies.length > 0) {
        setAssemblies(editData.assemblies);
      }
    }
  }, [editData]);

  const handleClientSelect = (clientId) => {
    const selected = clientOptions.find(c => c._id === clientId);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        client_id: selected._id,
        client_snapshot: {
          name: selected.name || "",
          attention_person: selected.attention_person || selected.name || "",
          email: selected.email || "",
          phone: selected.phone || "",
          address: selected.address || "",
          city: selected.city || "Surat",
          state: selected.state || "Gujarat",
          pincode: selected.pincode || "",
          gst_no: selected.gstNumber || ""
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        client_id: "",
        client_snapshot: { ...initialBomState.client_snapshot }
      }));
    }
  };

  // Live Reactive Financial Calculation
  const financials = useMemo(() => {
    let subtotal = 0;
    let totalAssemblies = 0;
    let totalComponents = 0;

    assemblies.forEach(ass => {
      const bQty = parseFloat(ass.quantity) || 1;
      totalAssemblies += bQty;
      subtotal += parseFloat(ass.total_price) || (parseFloat(ass.unit_rate) || 0) * bQty;

      (ass.sub_assemblies || []).forEach(sub => {
        totalComponents += (sub.items || []).length;
      });
    });

    const pack = parseFloat(formData.packaging_charges) || 0;
    const packGst = parseFloat(formData.packaging_gst_percent) || 18;
    const loading = parseFloat(formData.loading_charges) || 0;
    const loadingGst = parseFloat(formData.loading_gst_percent) || 18;
    const freight = parseFloat(formData.freight_charges) || 0;
    const freightGst = parseFloat(formData.freight_gst_percent) || 18;
    const other = parseFloat(formData.other_charges) || 0;
    const matGst = parseFloat(formData.gst_percent) || 18;

    const taxable = subtotal + pack + loading + freight + other;

    const firm = firmData?.find(f => f._id === formData.firm_id);
    const isSameState = firm?.state && formData.client_snapshot?.state &&
      firm.state.toLowerCase().trim() === formData.client_snapshot.state.toLowerCase().trim();

    const matTax = (subtotal * matGst) / 100;
    const packTax = (pack * packGst) / 100;
    const loadingTax = (loading * loadingGst) / 100;
    const freightTax = (freight * freightGst) / 100;
    const otherTax = (other * matGst) / 100;

    const totalTaxRaw = matTax + packTax + loadingTax + freightTax + otherTax;
    const totalTax = Number(totalTaxRaw.toFixed(2));

    let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

    if (isSameState) {
      cgstAmount = Number((totalTax / 2).toFixed(2));
      sgstAmount = Number((totalTax / 2).toFixed(2));
    } else {
      igstAmount = totalTax;
    }

    const grandRaw = taxable + totalTax;
    const grandTotal = Math.round(grandRaw);
    const roundOff = Number((grandTotal - grandRaw).toFixed(2));

    return {
      subtotal_amount: subtotal,
      packaging_charges: pack,
      loading_charges: loading,
      freight_charges: freight,
      total_assemblies: totalAssemblies,
      total_components: totalComponents,
      taxable_amount: taxable,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      total_tax: totalTax,
      round_off: roundOff,
      grand_total: grandTotal,
      amount_in_words: numberToWords(grandTotal)
    };
  }, [assemblies, formData, firmData]);

  const handleSubmit = () => {
    if (!formData.firm_id) {
      toast.error("Please select a Firm");
      setActiveTab("info");
      return;
    }
    if (!formData.client_snapshot?.name?.trim()) {
      toast.error("Please select or enter Client / Company Name");
      setActiveTab("info");
      return;
    }
    if (assemblies.length === 0) {
      toast.error("Please add at least one Major Assembly / Panel");
      setActiveTab("assemblies");
      return;
    }

    const payload = {
      ...formData,
      assemblies,
      is_new_revision: isNewRevision,
      revision_summary: revisionSummary
    };

    if (locationData?.id) {
      dispatch(updateAdminBom({ id: locationData.id, payload })).then((res) => {
        if (res?.payload?.success) {
          toast.success("Bill of Material updated successfully");
          navigate('/admin/bom-management');
        }
      });
    } else {
      dispatch(addAdminBom(payload)).then((res) => {
        if (res?.payload?.success) {
          toast.success("Bill of Material created successfully");
          navigate('/admin/bom-management');
        }
      });
    }
  };

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={() => setIsSidebarOpen(!isSidebarOpen)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content p-3">
          {/* Breadcrumb Header */}
          <div className="page-header mb-2">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <ul className="breadcrumb mb-0" style={{ fontSize: "12px" }}>
                  <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item"><i className="feather-chevron-right"></i></li>
                  <li className="breadcrumb-item"><Link to="/admin/bom-management">Bill of Materials</Link></li>
                  <li className="breadcrumb-item"><i className="feather-chevron-right"></i></li>
                  <li className="breadcrumb-item active">{locationData?.id ? "Edit" : "Create"} BOM</li>
                </ul>
              </div>
              <div className="col-sm-4 text-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate('/admin/bom-management')}
                  className="rounded py-1 px-2 d-inline-flex align-items-center gap-1"
                  style={{ fontSize: "11px" }}
                >
                  <ArrowLeft size={13} /> Back to List
                </Button>
              </div>
            </div>
          </div>

          {/* Form Wizard Navigation */}
          <div className="border rounded bg-white shadow-sm overflow-hidden mb-3">
            <div className="bg-light border-bottom p-0">
              <Nav variant="tabs" className="modern-tabs border-0 flex-nowrap" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav.Item>
                  <Nav.Link eventKey="info" className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold" style={{ fontSize: "12px" }}>
                    <Building2 size={14} /> 1. Client & Proposal Info
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="assemblies" className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold" style={{ fontSize: "12px" }}>
                    <Layers size={14} /> 2. Assemblies & BOQ Items ({assemblies.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="specs" className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold" style={{ fontSize: "12px" }}>
                    <Sliders size={14} /> 3. Technical Specs Matrix
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="terms" className="d-flex align-items-center gap-1 py-2 px-3 fw-semibold" style={{ fontSize: "12px" }}>
                    <FileText size={14} /> 4. Terms, Drawing & Costing
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <div className="p-3">
              {/* TAB 1: BASIC & CLIENT INFO */}
              {activeTab === "info" && (
                <div>
                  <Row className="g-2 mt-2">
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Firm / Company <span className="login-danger">*</span></label>
                        <select
                          className="form-select"
                          value={formData.firm_id}
                          onChange={(e) => setFormData({ ...formData, firm_id: e.target.value })}
                        >
                          <option value="">Select Firm</option>
                          {firmData?.map(f => (
                            <option key={f._id} value={f._id}>{f.name}</option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Proposal Date <span className="login-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Quotation / Inquiry Reference</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. INQ-2025-089"
                          value={formData.quotation_ref_no}
                          onChange={(e) => setFormData({ ...formData, quotation_ref_no: e.target.value })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Proposal Subject / Scope</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Quotation for 50KVAR APFC PANEL & Power Factor Improvement System"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-2">
                    <Col md={3}>
                      <div className="input-block local-forms custom-select-wpr">
                        <label>Select Client / Party (From Master)</label>
                        <Dropdown
                          value={formData.client_id}
                          onChange={(e) => handleClientSelect(e.value)}
                          options={clientOptions.map(c => ({
                            label: `${c.name} ${c.gstNumber ? `(${c.gstNumber})` : ''}`,
                            value: c._id
                          }))}
                          placeholder="Search or Select Client..."
                          filter
                          showClear={!!formData.client_id}
                          className="w-100"
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Client / Company Name <span className="login-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Client or Company Name"
                          value={formData.client_snapshot?.name || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, name: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Kind Attention / Contact Person</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Raj Purohit"
                          value={formData.client_snapshot?.attention_person || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, attention_person: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Email Address</label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="name@company.com"
                          value={formData.client_snapshot?.email || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, email: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Phone / Mobile</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="98XXXXXXXX"
                          value={formData.client_snapshot?.phone || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, phone: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Address / Plant Location</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Street / Industrial Estate"
                          value={formData.client_snapshot?.address || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, address: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>State <span className="login-danger">*</span></label>
                        <select
                          className="form-select"
                          value={formData.client_snapshot?.state || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              client_snapshot: {
                                ...formData.client_snapshot,
                                state: e.target.value,
                                city: "" // Reset city on state change
                              }
                            });
                          }}
                        >
                          <option value="">Select State</option>
                          {stateList?.map((st) => (
                            <option key={st.isoCode} value={st.name}>
                              {st.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>City</label>
                        <select
                          className="form-select"
                          value={formData.client_snapshot?.city || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              client_snapshot: {
                                ...formData.client_snapshot,
                                city: e.target.value
                              }
                            });
                          }}
                        >
                          <option value="">Select City</option>
                          {cityList?.map((ct) => (
                            <option key={ct.name} value={ct.name}>
                              {ct.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="input-block local-forms">
                        <label>Client GSTIN</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="24AAAAA0000A1Z5"
                          value={formData.client_snapshot?.gst_no || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_snapshot: { ...formData.client_snapshot, gst_no: e.target.value }
                          })}
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="primary" size="sm" onClick={() => setActiveTab("assemblies")} className="px-3 py-1 rounded fw-semibold" style={{ fontSize: "12px" }}>
                      Next: Build Assemblies & BOQ &rarr;
                    </Button>
                  </div>
                </div>
              )}

              {/* TAB 2: ASSEMBLIES & BOQ BUILDER */}
              {activeTab === "assemblies" && (
                <div>
                  <AssemblyTreeBuilder
                    assemblies={assemblies}
                    setAssemblies={setAssemblies}
                    itemMasterList={itemMasterList}
                  />
                  <div className="d-flex justify-content-between mt-3">
                    <Button variant="outline-secondary" size="sm" onClick={() => setActiveTab("info")} className="px-3 py-1 rounded" style={{ fontSize: "12px" }}>
                      &larr; Back to Client Info
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setActiveTab("specs")} className="px-3 py-1 rounded fw-semibold" style={{ fontSize: "12px" }}>
                      Next: Technical Specs Matrix &rarr;
                    </Button>
                  </div>
                </div>
              )}

              {/* TAB 3: TECHNICAL SPECIFICATIONS */}
              {activeTab === "specs" && (
                <div>
                  <TechnicalSpecMatrix
                    assemblies={assemblies}
                    setAssemblies={setAssemblies}
                  />
                  <div className="d-flex justify-content-between mt-3">
                    <Button variant="outline-secondary" size="sm" onClick={() => setActiveTab("assemblies")} className="px-3 py-1 rounded" style={{ fontSize: "12px" }}>
                      &larr; Back to Assemblies
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setActiveTab("terms")} className="px-3 py-1 rounded fw-semibold" style={{ fontSize: "12px" }}>
                      Next: Terms & Costing &rarr;
                    </Button>
                  </div>
                </div>
              )}

              {/* TAB 4: TERMS, DRAWING & COSTING */}
              {activeTab === "terms" && (
                <div>
                  <TermsAndDrawingTab
                    terms={formData.terms_and_conditions || []}
                    setTerms={(terms) => setFormData({ ...formData, terms_and_conditions: terms })}
                    drawing={formData.drawing_details || {}}
                    setDrawing={(dr) => setFormData({ ...formData, drawing_details: dr })}
                  />

                  <div className="mt-3">
                    <CostingTaxSummary
                      financials={financials}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </div>

                  {locationData?.id && (
                    <div className="mt-3 p-2 bg-light rounded border">
                      <div className="form-check mb-1">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="isNewRev"
                          checked={isNewRevision}
                          onChange={(e) => setIsNewRevision(e.target.checked)}
                        />
                        <label className="form-check-label fw-bold" htmlFor="isNewRev" style={{ fontSize: "12px" }}>
                          Save as New Revision (Increment Version)
                        </label>
                      </div>
                      {isNewRevision && (
                        <input
                          type="text"
                          className="form-control form-control-sm py-0"
                          style={{ height: "26px", fontSize: "11px" }}
                          placeholder="Revision change summary"
                          value={revisionSummary}
                          onChange={(e) => setRevisionSummary(e.target.value)}
                        />
                      )}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                    <Button variant="outline-secondary" size="sm" onClick={() => setActiveTab("specs")} className="px-3 py-1 rounded" style={{ fontSize: "12px" }}>
                      &larr; Back to Specs
                    </Button>
                    <div className="d-flex gap-2">
                      <Button variant="primary" size="sm" onClick={handleSubmit} className="px-3 py-1 rounded shadow-sm d-flex align-items-center gap-1 fw-semibold" style={{ fontSize: "12px" }}>
                        <Save size={14} /> {locationData?.id ? "Update Bill of Material" : "Save & Create BOM"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default ManageBom;
